/**
 * MultiKeyMap<K, V> - A generic data structure that allows storing multiple values under the same key
 * @template K - The type of the key
 * @template V - The type of the value
 */
class MultiKeyMap<K, V> {
	private map: Map<K, V[]>;

	constructor() {
		this.map = new Map<K, V[]>();
	}

	/**
	 * Add a value to a key
	 * @param key - The key to add to
	 * @param value - The value to add
	 */
	add(key: K, value: V): void {
		if (!this.map.has(key)) {
			this.map.set(key, []);
		}
		this.map.get(key)?.push(value);
	}

	/**
	 * Get all values for a specific key
	 * @param key - The key to search for
	 * @returns An array of values or undefined if the key doesn't exist
	 */
	get(key: K): V[] | undefined {
		return this.map.get(key);
	}

	/**
	 * Check if a key exists in the data structure
	 * @param key - The key to check
	 * @returns true if the key exists, otherwise false
	 */
	has(key: K): boolean {
		return this.map.has(key);
	}

	/**
	 * Delete all values for a specific key
	 * @param key - The key to delete
	 * @returns true if the key was successfully deleted, otherwise false
	 */
	delete(key: K): boolean {
		return this.map.delete(key);
	}

	/**
	 * Clear all data from the structure
	 */
	clear(): void {
		this.map.clear();
	}

	/**
	 * Get the number of unique keys in the data structure
	 * @returns The number of unique keys
	 */
	size(): number {
		return this.map.size;
	}

	/**
	 * Get all keys in the data structure
	 * @returns An array of all keys
	 */
	keys(): K[] {
		return Array.from(this.map.keys());
	}

	/**
	 * Get all values in the data structure
	 * @returns An array of all values (including duplicates)
	 */
	values(): V[] {
		return Array.from(this.map.values()).flat();
	}
}

globalThis.MultiKeyMap = MultiKeyMap;

export {};
